import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { parseHTML } from '../../utils/parseHMTL';
import { resolveFavicon, resolveTabTitle } from '../../utils/hostBranding';

interface IHelmet {
  companyName?: string;
  clientLogoURL?: string;
  primaryColor?: string;
  secondaryColor?: string;
  playerName?: string;
  actionTitle?: string;
  actionImages?: { url?: string }[];
  tabTitle?: string;
  clientFavicon?: string;
  googleAnalyticsIntegration?: boolean | string | undefined;
  pixelIntegration?: boolean | string | undefined;
  canonicalUrl?: string;
  pageDescription?: string;
}

export function HelmetComponent({
  companyName,
  clientLogoURL,
  primaryColor,
  secondaryColor,
  playerName,
  actionTitle,
  actionImages,
  clientFavicon,
  tabTitle,
  googleAnalyticsIntegration,
  pixelIntegration,
  canonicalUrl,
  pageDescription,
}: IHelmet) {
  const colors = {
    primaryColor: primaryColor || '#000000',
    secondaryColor: secondaryColor || '#ffffff',
  };

  const favicon = resolveFavicon(clientFavicon);

  // Valores padrão para Open Graph
  const siteName = companyName || 'OQ Digital';
  const ogTitle = tabTitle || actionTitle || siteName;
  const ogDescription =
    pageDescription ||
    (playerName && actionTitle
      ? `${playerName} tem um convite para você!`
      : actionTitle || 'Conheça esta oportunidade exclusiva');

  // URL da imagem - prioridade: actionImages > clientLogoURL > default
  const rawImageUrl = actionImages?.[0]?.url || clientLogoURL;

  // URL canônica
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const ogUrl = canonicalUrl || currentUrl;

  useEffect(() => {
    if (pixelIntegration) {
      pixelSequenceInject();
    }
  }, [
    ogUrl,
    ogDescription,
    ogTitle,
    siteName,
    actionImages,
    rawImageUrl,
    pixelIntegration,
  ]);

  function pixelSequenceInject() {
    try {
      const parsedPixel = parseHTML(`${pixelIntegration}`);

      if (parsedPixel.children[1]) {
        const noScriptTag = parsedPixel.children[1];
        noScriptTag.setAttribute('id', 'pixel-noscript');
        const imageTagTracking = noScriptTag.innerHTML.toString();
        noScriptTag.textContent = imageTagTracking;

        if (!document.querySelector('#pixel-noscript')) {
          document.getElementsByTagName('head')[0].appendChild(noScriptTag);
        }
      }

      if (parsedPixel.children[0]) {
        const simpleScript = parsedPixel.children[0];
        simpleScript.setAttribute('id', 'pixel-script');

        if (!document.querySelector('#pixel-script')) {
          document.getElementsByTagName('head')[0].appendChild(simpleScript);
        }
      }
    } catch (error) {
      console.error('Error injecting pixel:', error);
    }
  }

  return (
    <Helmet>
      {/* Google Analytics Integration */}
      {googleAnalyticsIntegration && (
        <>
          <script
            async
            src={
              parseHTML(`${googleAnalyticsIntegration}`)?.children[0]
                ?.attributes?.src?.value || ''
            }
          />
          <script>
            {parseHTML(`${googleAnalyticsIntegration}`)?.children[1]
              ?.innerHTML || ''}
          </script>
        </>
      )}

      {/* Meta Tags Básicas */}
      <title>
        {resolveTabTitle(companyName ? `${companyName} - ${ogTitle}` : ogTitle)}
      </title>
      <meta name="robots" content="noindex" />
      <meta name="description" content={ogDescription} />

      {favicon && <link rel="icon" href={favicon} />}

      <link rel="canonical" href={ogUrl} />

      {/* Open Graph Tags - APENAS UMA VEZ, SEM DUPLICATAS */}
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />

      {/* IMAGEM - APENAS UMA VEZ, VALIDADA E LIMPA */}
      <meta property="og:image" content={rawImageUrl} />
      <meta property="og:image:secure_url" content={rawImageUrl} />
      <meta property="og:image:alt" content={ogTitle} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="pt_BR" />

      {/* Facebook App ID */}
      <meta property="fb:app_id" content="596900164141274" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={rawImageUrl} />

      {/* CSS Variables */}
      <style type="text/css">
        {`
          :root {
            --primary-color: ${colors.primaryColor};
            --secondary-color: ${colors.secondaryColor};
          }
          
          .og-preview {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
          }
        `}
      </style>

      {/* Viewport */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=5.0"
      />
    </Helmet>
  );
}

/**
 * Limpa URLs removendo parâmetros e corrigindo barras duplas
 * @param url URL completa
 * @returns URL limpa ou vazio se inválida
 */
export function cleanGoogleStorageUrl(url?: string): string {
  if (!url) {
    return '';
  }

  try {
    // Remove parâmetros de query string
    let cleanUrl = url.split('?')[0];

    // 🔧 CORRIGE BARRAS DUPLAS (exceto em http:// ou https://)
    cleanUrl = cleanUrl.replace(/([^:]\/)\/+/g, '$1');

    // Valida se é uma URL válida
    new URL(cleanUrl);

    return cleanUrl;
  } catch (error) {
    console.error('⚠️ Invalid image URL:', url, error);
    return '';
  }
}

/**
 * Função auxiliar para debug - mostra todas as meta tags no console
 */
export function debugMetaTags() {
  if (typeof window === 'undefined') return;

  const metaTags = {
    'og:image': document
      .querySelector('meta[property="og:image"]')
      ?.getAttribute('content'),
    'og:title': document
      .querySelector('meta[property="og:title"]')
      ?.getAttribute('content'),
    'og:description': document
      .querySelector('meta[property="og:description"]')
      ?.getAttribute('content'),
    'og:url': document
      .querySelector('meta[property="og:url"]')
      ?.getAttribute('content'),
  };

  console.log('📋 Meta Tags Atuais:', metaTags);

  // Verifica duplicatas
  const ogImages = document.querySelectorAll('meta[property="og:image"]');
  if (ogImages.length > 1) {
    console.warn(
      '⚠️ AVISO: Encontradas',
      ogImages.length,
      'meta tags og:image duplicadas!'
    );
    ogImages.forEach((tag, i) => {
      console.log(`  ${i + 1}:`, tag.getAttribute('content'));
    });
  }
}
